import { addBreadcrumb } from "@sentry/gatsby";
import { useQuery } from "@tanstack/react-query";
import type { UserLocationLookupResponse } from "../api/user-location-lookup";

const isUserLocationData = (
	json: unknown,
): json is UserLocationLookupResponse => {
	return (
		typeof json === "object" &&
		json !== null &&
		"ip" in json &&
		"code" in json &&
		"name" in json
	);
};

const fetchUserLocation = async (): Promise<
	UserLocationLookupResponse | undefined
> => {
	try {
		const response = await fetch("/api/user-location-lookup");

		if (!response.ok) {
			throw new Error(`Request failed with status: ${response.status}`);
		}

		const json = (await response.json()) as unknown;

		if (isUserLocationData(json)) {
			return json;
		}

		throw new Error("Invalid response format from user location lookup");
	} catch (error) {
		console.error("Failed to fetch user location:", error);

		// Add breadcrumb to Sentry, but do not throw error.
		addBreadcrumb({
			level: "error",
			message: "Failed to get user location from external service",
			data: {
				error: error instanceof Error ? error.message : String(error),
			},
		});

		return undefined;
	}
};

export const useUserLocation = () => {
	return useQuery({
		queryKey: ["user-location"],
		queryFn: fetchUserLocation,
		staleTime: Infinity,
	});
};
